'use client';

import Link from 'next/link';
import { LocaleLink } from '@/core/i18n';
import { cn } from '@/core/utils/cn';
import {
  TrendingUp,
  Users,
  Shield,
  CheckCircle2,
  ArrowRight,
  BadgeDollarSign,
  Headphones,
  BookOpen,
  Building2,
  type LucideIcon,
} from 'lucide-react';
import { usePageContent } from '@/core/i18n/content';
import { PARTNER_CONTENT } from '@/core/i18n/content/partner.content';
import type { PartnerContent, PartnerBenefit as PartnerBenefitData } from '@/core/i18n/content/partner.types';
import { useTranslation } from '@/core/i18n';

const BENEFIT_ICONS: LucideIcon[] = [BadgeDollarSign, Headphones, BookOpen, Users, Shield, Building2];

/* -------------------------------------------------------------------------- */
/*  Sub-components                                                            */
/* -------------------------------------------------------------------------- */

function BenefitCard({ benefit, icon, index }: { benefit: PartnerBenefitData; icon: LucideIcon; index: number }) {
  const { t } = useTranslation();
  const Icon = icon;
  return (
    <div
      id={`partner-benefit-${index}`}
      className={cn(
        'group rounded-xl border border-hms-border bg-hms-card p-6',
        'transition-all duration-300',
        'hover:border-hms-gold/20 hover:-translate-y-1 hover:shadow-lg hover:shadow-black/20',
      )}
    >
      <div
        className={cn(
          'mb-4 flex h-12 w-12 items-center justify-center rounded-full',
          'bg-hms-gold/10 transition-colors duration-300 group-hover:bg-hms-gold/15',
        )}
      >
        <Icon className="h-5 w-5 text-hms-gold" />
      </div>
      <h3
        id={`partner-benefit-title-${index}`}
        className="mb-2 text-base font-bold text-hms-text tracking-tight"
      >
        {benefit.title}
      </h3>
      <p className="text-sm leading-relaxed text-hms-textSub">
        {benefit.description}
      </p>
    </div>
  );
}

function TierCard({ tier, index }: { tier: PartnerContent['tiers']['items'][number]; index: number }) {
  return (
    <div
      id={`partner-tier-${index}`}
      className={cn(
        'rounded-xl border p-6 flex flex-col',
        tier.highlight
          ? 'border-hms-gold/40 bg-hms-gold/5 shadow-lg shadow-hms-gold/10'
          : 'border-hms-border bg-hms-card',
      )}
    >
      {tier.badge && (
        <span className="mb-3 inline-flex self-start rounded-full bg-hms-gold/15 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-hms-gold">
          {tier.badge}
        </span>
      )}
      <h3 className="text-lg font-bold text-hms-text">{tier.name}</h3>
      <p className={cn(
        'mt-2 text-3xl font-extrabold tracking-tight',
        'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400',
        'bg-clip-text text-transparent',
      )}>
        {tier.commission}
      </p>
      <p className="text-xs text-hms-textDim mt-1">{tier.commLabel}</p>
      <ul className="mt-5 flex-1 space-y-2.5">
        {tier.requirements.map((req) => (
          <li key={req} className="flex items-start gap-2.5">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-hms-green" />
            <span className="text-sm text-hms-textSub">{req}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  PartnerProgramPage                                                        */
/* -------------------------------------------------------------------------- */

export function PartnerProgramPage() {
  const c = usePageContent(PARTNER_CONTENT);

  return (
    <main id="partner-program-page">
      {/* ── Hero ── */}
      <section id="partner-hero" className="relative w-full overflow-hidden bg-hms-bg">
        <div className="pointer-events-none absolute inset-0" style={{ background: 'radial-gradient(ellipse 80% 60% at 50% 30%, rgba(200,145,58,0.08) 0%, transparent 70%)' }} />
        <div className="relative mx-auto max-w-4xl px-4 pb-16 pt-24 text-center sm:px-6 sm:pt-32 lg:px-8 lg:pt-40">
          <h1 id="partner-hero-title" className={cn('font-poppins text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl lg:text-6xl', 'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent')}>{c.hero.title}</h1>
          <p id="partner-hero-subtitle" className="mx-auto mt-6 max-w-2xl text-base text-hms-textSub sm:text-lg">{c.hero.subtitle}</p>
          <div id="partner-hero-actions" className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
            <LocaleLink id="partner-hero-apply-btn" href="/contact" className={cn('inline-flex items-center gap-2 rounded-lg bg-hms-gold px-8 py-3.5', 'text-sm font-semibold tracking-wide text-hms-bg', 'shadow-lg shadow-hms-gold/20 transition-all duration-200', 'hover:bg-hms-goldLt hover:shadow-hms-gold/30 active:scale-[0.98]', 'w-full sm:w-auto justify-center')}>
              {c.hero.applyBtn}<ArrowRight className="h-4 w-4" />
            </LocaleLink>
            <LocaleLink id="partner-hero-learn-btn" href="/features" className={cn('inline-flex items-center justify-center rounded-lg border border-hms-border px-8 py-3.5', 'text-sm font-semibold tracking-wide text-hms-text', 'transition-all duration-200 hover:border-hms-gold/40 hover:text-hms-gold', 'w-full sm:w-auto')}>{c.hero.exploreBtn}</LocaleLink>
          </div>
        </div>
      </section>

      {/* ── Partner Benefits ── */}
      <section id="partner-benefits" className="relative w-full overflow-hidden bg-hms-bg py-20 sm:py-28">
        <div className="pointer-events-none absolute inset-0" style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 40%, rgba(200,145,58,0.04) 0%, transparent 70%)' }} />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 id="partner-benefits-title" className={cn('text-3xl font-extrabold tracking-tight sm:text-4xl', 'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400', 'bg-clip-text text-transparent')}>{c.benefits.title}</h2>
            <p className="mt-4 text-base text-hms-textSub sm:text-lg">{c.benefits.subtitle}</p>
          </div>
          <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {c.benefits.items.map((benefit, idx) => (
              <BenefitCard key={idx} benefit={benefit} icon={BENEFIT_ICONS[idx]} index={idx} />
            ))}
          </div>
        </div>
      </section>

      {/* ── Commission Tiers ── */}
      <section id="partner-tiers" className="relative w-full overflow-hidden bg-hms-surface py-20 sm:py-28">
        <div className="pointer-events-none absolute inset-0" style={{ background: 'radial-gradient(ellipse 60% 50% at 50% 50%, rgba(200,145,58,0.05) 0%, transparent 70%)' }} />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 id="partner-tiers-title" className={cn('text-3xl font-extrabold tracking-tight sm:text-4xl', 'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400', 'bg-clip-text text-transparent')}>{c.tiers.title}</h2>
            <p className="mt-4 text-base text-hms-textSub sm:text-lg">{c.tiers.subtitle}</p>
          </div>
          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
            {c.tiers.items.map((tier, idx) => (
              <TierCard key={idx} tier={tier} index={idx} />
            ))}
          </div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section id="partner-how-it-works" className="relative w-full overflow-hidden bg-hms-bg py-20 sm:py-28">
        <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 id="partner-how-title" className={cn('text-3xl font-extrabold tracking-tight sm:text-4xl', 'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400', 'bg-clip-text text-transparent')}>{c.howItWorks.title}</h2>
            <p className="mt-4 text-base text-hms-textSub sm:text-lg">{c.howItWorks.subtitle}</p>
          </div>
          <div className="mt-14 space-y-6">
            {c.howItWorks.steps.map((item, idx) => (
              <div key={idx} id={`partner-step-${idx + 1}`} className="flex items-start gap-5 rounded-xl border border-hms-border bg-hms-card p-6">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-hms-gold/10">
                  <span className="text-sm font-bold text-hms-gold">{idx + 1}</span>
                </div>
                <div>
                  <h3 className="text-base font-bold text-hms-text">{item.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-hms-textSub">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Who Can Join ── */}
      <section id="partner-who" className="relative w-full overflow-hidden bg-hms-surface py-20 sm:py-28">
        <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 id="partner-who-title" className={cn('text-3xl font-extrabold tracking-tight sm:text-4xl', 'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400', 'bg-clip-text text-transparent')}>{c.whoCanJoin.title}</h2>
            <p className="mt-4 text-base text-hms-textSub sm:text-lg">{c.whoCanJoin.subtitle}</p>
          </div>
          <div className="mx-auto mt-10 max-w-xl space-y-3">
            {c.whoCanJoin.items.map((item, idx) => (
              <div key={idx} id={`partner-who-item-${idx}`} className="flex items-center gap-3 rounded-lg border border-hms-border bg-hms-card px-5 py-3.5">
                <CheckCircle2 className="h-4 w-4 shrink-0 text-hms-green" />
                <span className="text-sm font-medium text-hms-textSub">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section id="partner-cta" className="relative w-full overflow-hidden bg-hms-bg py-20 sm:py-28">
        <div className="pointer-events-none absolute inset-0" style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 50%, rgba(200,145,58,0.06) 0%, transparent 70%)' }} />
        <div className="relative mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
          <TrendingUp className="mx-auto mb-4 h-8 w-8 text-hms-gold" />
          <h2 id="partner-cta-title" className={cn('text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl', 'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400', 'bg-clip-text text-transparent')}>{c.cta.title}</h2>
          <p className="mx-auto mt-5 max-w-xl text-base text-hms-textSub sm:text-lg">{c.cta.subtitle}</p>
          <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
            <LocaleLink id="partner-cta-apply-btn" href="/contact" className={cn('inline-flex items-center gap-2 rounded-lg bg-hms-gold px-8 py-3.5', 'text-sm font-semibold tracking-wide text-hms-bg', 'shadow-lg shadow-hms-gold/20 transition-all duration-200', 'hover:bg-hms-goldLt hover:shadow-hms-gold/30 active:scale-[0.98]', 'w-full sm:w-auto justify-center')}>
              {c.cta.applyBtn}<ArrowRight className="h-4 w-4" />
            </LocaleLink>
            <LocaleLink id="partner-cta-contact-btn" href="/contact" className={cn('inline-flex items-center justify-center rounded-lg border border-hms-border px-8 py-3.5', 'text-sm font-semibold tracking-wide text-hms-text', 'transition-all duration-200 hover:border-hms-gold/40 hover:text-hms-gold', 'w-full sm:w-auto')}>{c.cta.contactBtn}</LocaleLink>
          </div>
          <p className="mt-5 text-xs text-hms-textDim">{c.cta.note}</p>
        </div>
      </section>
    </main>
  );
}
